"use client";

import { Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { getCurrencyDisplayName } from "@/lib/currency-display";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { Button } from "@/ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/components/ui/popover";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";

const sizeClass: Record<Size, string> = {
	sm: "text-sm",
	md: "text-base",
	lg: "text-2xl font-semibold tracking-tight",
};

export function ReferencePrimaryPrice({
	presentation,
	locale,
	channel: _channel,
	size = "md",
	className,
	showReferenceTooltip = true,
}: {
	presentation: ResolvedMoneyPresentation;
	locale: string;
	channel: string;
	size?: Size;
	className?: string;
	/** If false, only the primary label is shown (no info popover for reference currency). */
	showReferenceTooltip?: boolean;
}) {
	const t = useTranslations("pricing");
	const checkoutName = getCurrencyDisplayName(presentation.checkoutCurrencyCode, locale);

	if (!presentation.showDual || !showReferenceTooltip) {
		return <span className={cn(sizeClass[size], className)}>{presentation.primaryLabel}</span>;
	}

	return (
		<span className={cn("inline-flex flex-wrap items-baseline gap-1.5", className)}>
			<span className={cn(sizeClass[size], "tabular-nums")}>{presentation.primaryLabel}</span>
			<Popover>
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-7 w-7 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
						aria-label={t("infoIconAria")}
					>
						<Info className="h-3.5 w-3.5" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="space-y-2 text-sm" align="start">
					<p className="font-medium tabular-nums text-foreground">{presentation.canonicalLabel}</p>
					<p className="leading-snug text-muted-foreground">
						{t("referenceTooltip", { currency: checkoutName })}
					</p>
				</PopoverContent>
			</Popover>
		</span>
	);
}
